<script>
import { mapGetters } from 'vuex'
import BaseDataForm from '@/components/profile/billingData/BaseDataForm.vue'
import DocumentValidationStatusFilter from './filter/DocumentValidationStatusFilter.vue'
import DocumentValidationCostCenterFilter from './filter/DocumentValidationCostCenterFilter.vue'
import DocumentValidationDataTable from './documentValidationDataTable/DocumentValidationDataTable.vue'
import DocumentValidationNextStep from './DocumentValidationNextStep.vue'
import CustomAlert from '@/components/common/CustomAlert'
export default {
  components: {
    DocumentValidationStatusFilter,
    DocumentValidationCostCenterFilter,
    DocumentValidationDataTable,
    DocumentValidationNextStep,
    BaseDataForm,
    CustomAlert
  },
  props: {
    nextStep: { type: Function },
    order: { type: Object } // comes from wizard-view
  },
  data: vm => ({
    documents: [],
    failedDocuments: [],
    visibleDocumentStatus: {
      [vm.$enums.validationStatus.pending]: true,
      [vm.$enums.validationStatus.userActionRequired]: true,
      [vm.$enums.validationStatus.success]: true,
      [vm.$enums.validationStatus.error]: true
    },
    visibleCostCenter: [],
    loading: true,
    error: false,
    errorContent: '',
    pollingOrderStatus: null,
    pollingOrderStatusInterval: 5000, // in milliseconds
    isCalling: false,
    isEmpty: false,
    showUserProfileRequired: false,
    loadingConfirmWarnings: false,
    userActionRequired: {}
  }),
  computed: {
    ...mapGetters(['locale']),
    isDisabled() {
      if (this.documents.length <= 0) return true
      return this.failedDocuments.length > 0 ? true : false
    },
    userActionRequiredDisabled() {
      return (
        this.documents.filter(
          document =>
            document.validationStatus === this.$enums.validationStatus.pending
        ).length > 0
      )
    }
  },
  created: async function () {
    if (this.order === null) {
      this.isEmpty = true
      return
    }

    await this.getValidation()
    // poll check if orderStatus < 3 (not validated)
    if (
      this.order.orderStatus < this.$enums.OrderStatus.validated &&
      this.loading
    ) {
      this.startPolling()
    } else {
      this.loading = false
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    getUniqCostCenter() {
      let set = new Set(this.documents.map(document => document.costCenter))
      let setValues = set.values()
      return Array.from(setValues)
    },
    returnedDocuments(value) {
      this.documents = value.validationResults // returned documents from DocumentDataTable
      this.visibleCostCenter = this.getUniqCostCenter() // preselect all costCenter
      this.userActionRequired = value.userActionRequired
      // check if order is empty or has errors
      if (this.documents.length <= 0) {
        // order has errors, show error-message
        if (this.order.orderStatus == this.$enums.OrderStatus.error) {
          this.showError('component.documentValidation.validation.documents.error')
        } else {
          // show empty-message
          this.isEmpty = true
        }
      }
      const { userActionRequired, error, pending } =
        this.$enums.validationStatus
      this.failedDocuments = this.documents.filter(document =>
        [userActionRequired, error, pending].includes(document.validationStatus)
      )
      if (this.failedDocuments.length <= 0) {
        this.$root.$emit('onValidated', this.order.orderId)
      }
    },
    getConfirmWarningsIds() {
      const { userActionRequired, error } = this.$enums.validationStatus
      return this.documents
        .filter(document =>
          [userActionRequired, error].includes(document.validationStatus)
        )
        .reduce((acc, value) => {
          acc.push(value.documentId)
          return acc
        }, [])
    },
    confirmWarnings() {
      this.loadingConfirmWarnings = true
      let documentIds = this.getConfirmWarningsIds()
      this.$uploadService
        .confirmWarnings({
          OrderId: this.order.orderId,
          DocumentIds: documentIds,
          PostageOnCover: true,
          ConfirmProductionArea: true,
          ConfirmFontWarnings: true,
          DeleteDuplicates: true,
          DeleteUnapprovedFiles: true
        })
        .then(() => {
          this.updateDataTableDocuments(documentIds)
        })
        .catch(() => {})
        .finally(() => {
          this.loadingConfirmWarnings = false
        })
    },
    updateDataTableDocuments(documentIds) {
      documentIds.forEach(documentId => {
        this.$refs.dataTable.documentUpdated({
          updated: true,
          document: this.documents.find(doc => doc.documentId === documentId)
        })
      })
    },
    getValidation() {
      this.isCalling = true
      this.error = false
        this.$uploadService
        .validation({ orderIds: [this.order.orderId] })
        .then(validationResponse => {
          if (
            validationResponse.validationResponseStatus ===
            this.$enums.validationResponseStatus.IsAvailable
          ) {
            this.loading = false
            this.stopPolling()
          }
          if (
            validationResponse.validationResponseStatus ===
            this.$enums.validationResponseStatus.Error) {
            this.showError('component.documentValidation.validation.documents.error')
          }
        })
        .catch(() => {this.showError('component.documentValidation.loading.documents.error')})
        .finally(() => {
          this.isCalling = false
        })
    },
    showError(cmsErrorName) {
      this.stopPolling()
      this.loading = false
      this.error = true
      this.errorContent = this.$t(cmsErrorName)
    },
    startPolling() {
      if (this.pollingOrderStatus === null) {
        this.pollingOrderStatus = setInterval(() => {
          // skip call when request is still calling (pending-call)
          if (!this.isCalling) this.getValidation()
        }, this.pollingOrderStatusInterval)
      }
    },
    stopPolling() {
      clearInterval(this.pollingOrderStatus)
      this.pollingOrderStatus = null
    },
    submittedBaseDataForm(event) {
      // event was fired with true
      if (event) {
        this.showUserProfileRequired = false
        this.$refs.DocumentValidationNextStep.setPaymentOpen()
      }
    },
    exportCSV(order) {
      this.$ordersService.exportCSV(order?.orderId, order?.organizationId)
        .then(response => {
          this.$ordersService.clientCSVDownload(response,order?.orderId)
        })
        .catch((error) => {
          console.error('Error exporting CSV', error)
        });
    }
  }
}
</script>
<template>
  <v-row v-if="isEmpty">
    <v-col>
      <v-row justify="space-around">
        <v-col class="text-center" lg="6">
          <v-card elevation="0">
            <v-card-text>
              <h3 class="primary--text">
                {{ $t('component.documentValidation.empty.documents.header') }}
              </h3>
              <br />
              <v-alert color="secondary" dark outlined>
                <div class="pb-2">
                  {{ $t('component.documentValidation.empty.documents.content') }}
                </div>
                <div>
                  {{ $t('component.documentValidation.empty.documents.subcontent.before') }}
                  <v-btn
                    :to="{ name: 'upload', params: { locale: locale } }"
                    color="primary"
                    small >
                    {{ $t('component.documentValidation.empty.documents.subcontent.link') }}
                  </v-btn>
                  {{ $t('component.documentValidation.empty.documents.subcontent.after') }}
                </div>
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
  <v-row v-else>
    <v-dialog
      v-model="showUserProfileRequired"
      transition="dialog-bottom-transition"
      width="800">
      <v-card>
        <v-card-title class="wordBreak justify-center"
        ><h1 class="headline primary--text text--primary-2 wordBreak">
          {{ $t('component.documentValidation.base.data.form.dialog.title') }}
        </h1></v-card-title >
        <v-divider></v-divider>
        <v-card-text class="pt-2">
          <CustomAlert :show-alert="true" alert-type="error">
            <span class="body-2">{{$t('component.documentValidation.base.data.form.dialog.text') }}</span>
          </CustomAlert>
          <BaseDataForm
            :hide-info-text="true"
            :shrink="true"
            @submitted="submittedBaseDataForm"/>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions
        ><v-btn color="error" @click="showUserProfileRequired = false">{{
            $t('button.cancel') }}</v-btn>
          <v-spacer></v-spacer
          ></v-card-actions>
      </v-card>
    </v-dialog>
    <v-col v-if="loading" cols="12">
      <v-row>
        <v-col class="text-center">
          <v-card elevation="0">
            <v-card-text>
              <h3 class="primary--text">
                <v-progress-circular
                  indeterminate
                  color="primary"
                ></v-progress-circular>
                {{ $t('component.documentValidation.loading.documents.header') }}
              </h3>
              <br />
              <v-alert
                dense
                outlined
                color="primary"
                icon="mdi-information-outline"
                transition="fade-transition">
                <span>{{ $t('component.documentValidation.loading.documents.content') }}</span>
                <br /><br />
                <span>{{ $t('component.documentValidation.loading.documents.subcontent') }}</span>
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-col>
    <v-col v-else-if="!loading && error" cols="md-8" class="center-text">
      <v-row>
        <v-col cols="6" class="mx-auto">
          <v-alert type="error">
            {{ errorContent }}
          </v-alert>
        </v-col>
      </v-row>
    </v-col>
    <v-col v-else cols="12" lg="8">
      <v-card-title>
        <h4 class="text-center wordBreak">
          {{ $t('component.documentValidation.document.validation.header') }}
        </h4>
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-card :loading="loading" outlined elevation="0">
          <v-toolbar color="primary" dark height="48">
            <v-spacer />
            <v-toolbar-title class="text-center resetWhitespace">
              <h2 class="headline text--primary-2" style="display:inline;">
                {{ $t('component.documentValidation.header.results') }}
              </h2>
              <span style="position:absolute;right:12px;display:inline;">
                 <v-tooltip content-class="dark_tip" top>
                  <template #activator="{ on }">
                      <v-icon
                        class="mr-2"
                        dark
                        v-on="on"
                        @click="exportCSV(order)"
                      >mdi-table-arrow-down</v-icon>
                  </template>
                  <span>{{ $t('component.documentValidation.tooltip.export.csv') }}</span>
                </v-tooltip>
              </span>
            </v-toolbar-title>
            <v-spacer />
          </v-toolbar>
          <v-card-text>
            {{ $t('component.documentValidation.header.filter') }}
            <DocumentValidationStatusFilter
              :documents="documents"
              :visible-document-status="visibleDocumentStatus"/>
            <DocumentValidationCostCenterFilter
              v-model="visibleCostCenter"
              :items="getUniqCostCenter()"/>
          </v-card-text>
          <v-divider />
          <v-card-text>
            {{ $t('component.documentValidation.header.data.table') }}
            <DocumentValidationDataTable
              ref="dataTable"
              :visible-document-status="visibleDocumentStatus"
              :visible-cost-center="visibleCostCenter"
              :order="order"
              @returnDocuments="returnedDocuments"/>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-col>
    <v-col cols="12" lg="4">
      <DocumentValidationNextStep
        ref="DocumentValidationNextStep"
        :next-step="nextStep"
        :is-disabled="isDisabled"
        :empty-list="documents.length <= 0"
        :loading="loading"
        :loading-confirm-warnings="loadingConfirmWarnings"
        :order="order"
        :user-action-required="userActionRequired"
        :user-action-required-disabled="userActionRequiredDisabled"
        @confirmWarnings="confirmWarnings"
        @update:userProfileRequired="showUserProfileRequired = true"/>
    </v-col>
  </v-row>
</template>
